/**
 * Spec 036 P8: maps a (possibly truncated) `ChartModel` onto the renderer's
 * `DrawModel`, and computes the readout for scrub bar `k`. Pure.
 */
import type { DrawMarker, QuantChartModel as DrawModel } from '@/ui/lib/quantChart/types'
import { lowerBound } from '@/ui/lib/quantChart/mapping'
import { snapEquityToCandles } from '@/ui/lib/quantChart/mapping'

import { candleSeries } from './chartReplay'
import type { CandlePoint, ChartModel, ValuePoint } from './types'

/**
 * Price decimals for a run chart, from the magnitude of the last close: real
 * FX series (EUR/USD near 1.10) need five, index and metal prices two.
 */
export function priceDigits(candles: readonly CandlePoint[]): number {
  const last = candles.length > 0 ? Math.abs(candles[candles.length - 1].close) : 0
  if (last === 0 || last >= 100) return 2
  return last >= 10 ? 3 : 5
}

function isCandle(p: CandlePoint | ValuePoint): p is CandlePoint {
  return 'open' in p
}

function candlesOf(model: ChartModel): CandlePoint[] {
  return (candleSeries(model)?.points ?? []).filter(isCandle)
}

export interface ChartToDrawOptions {
  /**
   * The run's stored `equity_curve` (spec 038 20.6). Used only when the chart
   * itself has no equity line series and the curve has at least 2 points.
   */
  equityCurve?: ReadonlyArray<readonly [number, number]> | null
}

/** The renderer model, or `null` when the chart has no candlestick series. */
export function chartToDraw(model: ChartModel, opts?: ChartToDrawOptions): DrawModel | null {
  const main = candleSeries(model)
  if (!main) return null
  const candles = candlesOf(model)
  if (candles.length === 0) return null
  const times = candles.map((c) => c.time)

  const levels: DrawModel['levels'] = []
  const zones: DrawModel['zones'] = []
  const markers: DrawMarker[] = []
  for (const list of Object.values(model.levels)) {
    for (const l of list) {
      levels.push({
        price: l.price,
        timeStart: l.time_start,
        timeEnd: l.time_end,
        color: l.color ?? 'paper',
        dashed: l.style === 'dashed',
        label: l.title,
      })
    }
  }
  for (const list of Object.values(model.zones)) {
    for (const z of list) {
      zones.push({
        timeStart: z.time_start,
        timeEnd: z.time_end,
        low: z.price_low,
        high: z.price_high,
        color: z.color ?? 'paper',
        state: z.state,
      })
    }
  }
  const circles: DrawModel['circles'] = []
  for (const list of Object.values(model.circles)) {
    for (const c of list) {
      circles.push({
        timeStart: c.time_start,
        timeEnd: c.time_end,
        low: c.price_low,
        high: c.price_high,
        color: c.color ?? 'paper',
        text: c.text,
      })
    }
  }
  for (const list of Object.values(model.markers)) {
    for (const m of list) {
      const at = lowerBound(times, m.time)
      const bar = candles[Math.min(at, candles.length - 1)]
      markers.push({
        time: m.time,
        above: m.position === 'aboveBar',
        kind: 'signal',
        text: m.text ?? '',
        price: bar.close,
        color: m.color,
      })
    }
  }

  const lines: DrawModel['lines'] = []
  let equity: DrawModel['equity'] = null
  for (const s of model.series) {
    if (s.type !== 'line') continue
    const points = s.points.filter((p): p is ValuePoint => !isCandle(p))
    if (s.pane === main.pane) {
      const byTime = new Map(points.map((p) => [p.time, p.value]))
      lines.push({ id: s.id, color: s.color ?? 'accent', values: candles.map((c) => byTime.get(c.time) ?? null) })
    } else if (equity === null) {
      equity = points.map((p) => ({ time: p.time, value: p.value }))
    }
  }

  // Rule 1: the chart's own equity series wins. Rule 2: else a stored curve of 2+ points, snapped to
  // the candle grid (and so truncated with the scrubber for free). Rule 3: else no equity pane.
  const curve = opts?.equityCurve
  if (equity === null && Array.isArray(curve) && curve.length >= 2) {
    const snapped = snapEquityToCandles(
      curve.map((p) => ({ time: p[0], value: p[1] })),
      times,
    )
    if (snapped.length > 0) equity = snapped
  }

  return {
    candles: candles.map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })),
    levels,
    zones,
    markers,
    lines,
    bands: [],
    circles,
    sessions: [],
    legend: [],
    equity,
    digits: priceDigits(candles),
  }
}

export interface ChartReadout {
  bar: number
  total: number
  time: string
  open: number
  high: number
  low: number
  close: number
  equity: number | null
  fill: string
  /** Price decimals for open/high/low/close. */
  digits: number
}

/** Readout for 0-based candle `k` of the FULL model, or `null` without candles. */
export function readoutAt(model: ChartModel, k: number, opts?: ChartToDrawOptions): ChartReadout | null {
  const candles = candlesOf(model)
  if (candles.length === 0) return null
  const idx = Math.min(candles.length - 1, Math.max(0, Math.floor(k)))
  const c = candles[idx]
  const digits = priceDigits(candles)
  let equity: number | null = null
  let ownEquity = false
  for (const s of model.series) {
    if (s.type !== 'line' || s.pane === candleSeries(model)?.pane) continue
    ownEquity = true
    for (const p of s.points) if (!isCandle(p) && p.time <= c.time) equity = p.value
    break
  }
  // Same source as the pane (chartToDraw rules 1 and 2): with no equity series of its own, the stored
  // curve snapped to the candle grid; the last value at or before this bar.
  const curve = opts?.equityCurve
  if (!ownEquity && Array.isArray(curve) && curve.length >= 2) {
    const snapped = snapEquityToCandles(
      curve.map((p) => ({ time: p[0], value: p[1] })),
      candles.map((x) => x.time),
    )
    for (const p of snapped) if (p.time <= c.time) equity = p.value
  }
  let fill = '—'
  for (const list of Object.values(model.markers)) {
    const hit = list.find((m) => m.time === c.time)
    if (hit) {
      const side = hit.text === 'BUY' || hit.text === 'SELL' ? hit.text : hit.position === 'aboveBar' ? 'SELL' : 'BUY'
      fill = `${side} @ ${c.close.toFixed(digits)}`
      break
    }
  }
  return {
    bar: idx + 1,
    total: candles.length,
    time: new Date(c.time * 1000).toISOString().replace('.000Z', 'Z'),
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    equity,
    fill,
    digits,
  }
}

/** Number of candles in the first candlestick series (0 when none). */
export function candleCount(model: ChartModel): number {
  return candlesOf(model).length
}
